import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { Button } from '@dashboard/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@dashboard/components/ui/popover';
import useMountEffect from '@dashboard/hooks/useMountEffect';
import useTheme from '@dashboard/hooks/useTheme';
import { getThemePreference, setThemePreference } from '@dashboard/lib/theme';
import { tracking } from '@dashboard/lib/tracking';
import { cn } from '@dashboard/lib/utils';
import { fetcher } from '@dashboard/services/request';
import {
  type Outcome,
  type TrickKey,
  dismissTrickOrTreat,
  isTrickOrTreatShown,
  pickOutcome,
} from './outcomes';
import { TRICKS } from './tricks';

async function newestCheckoutPath(sellerId: string, token: string) {
  try {
    const pages = await fetcher(`/sellers/${sellerId}/pages?limit=1&type=checkout`, token);
    return pages?.[0] ? `/pages/${pages[0]._id}/customize/order-bumps` : '/checkouts';
  } catch {
    return '/checkouts';
  }
}

export default function TrickOrTreat({ className }: { className?: string }) {
  const { seller, sellerId, token } = useAuth();
  const history = useHistory();
  const { theme } = useTheme();
  const [isShown, setIsShown] = useState(isTrickOrTreatShown);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isTricking, setIsTricking] = useState(false);
  const trick = useRef<AbortController | null>(null);

  useMountEffect(() => () => trick.current?.abort());

  if (!isShown) return null;

  const stopTrick = () => trick.current?.abort();

  const knock = () => {
    const next = pickOutcome(seller, outcome?.key ?? null);
    setOutcome(next);
    setIsOpen(true);
    tracking.trackEvent('trick_or_treat_clicked', { outcome: next.key });
    if (next.kind === 'trick') {
      const controller = new AbortController();
      trick.current = controller;
      setIsTricking(true);
      TRICKS[next.key as TrickKey](controller.signal).finally(() => {
        if (trick.current === controller) setIsTricking(false);
      });
    }
  };

  const close = () => {
    stopTrick();
    setIsOpen(false);
  };

  const takeTreat = async () => {
    setIsOpen(false);
    if (outcome?.key === 'orderBumps') history.push(await newestCheckoutPath(sellerId, token));
    if (outcome?.key === 'linkInBio') history.push('/link-in-bio');
    if (outcome?.key === 'halloweenTheme') setThemePreference('halloween');
  };

  return (
    <Popover open={isOpen} onOpenChange={open => !open && close()}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={event => {
            event.preventDefault();
            knock();
          }}
          disabled={isTricking}
          aria-label="Trick or treat"
          title="Trick or treat"
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md text-lg transition-transform hover:scale-110 hover:-rotate-6 hover:bg-sidebar-accent disabled:opacity-50 disabled:hover:scale-100 disabled:hover:rotate-0',
            className,
          )}
        >
          <span aria-hidden="true">🎃</span>
        </button>
      </PopoverTrigger>
      {outcome && (
        <PopoverContent align="start" className="w-80 p-4">
          <p className="font-medium">{outcome.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{outcome.body}</p>
          {theme === 'halloween' && (
            <div className="mt-3 flex items-center justify-between gap-2 rounded-md bg-muted px-3 py-2 text-xs">
              <span>Your dashboard is in costume.</span>
              <button
                type="button"
                className="font-medium underline-offset-2 hover:underline"
                onClick={() => setThemePreference(getThemePreference())}
              >
                Take it off
              </button>
            </div>
          )}
          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => {
                dismissTrickOrTreat();
                close();
                setIsShown(false);
              }}
            >
              Hide until next year
            </button>
            {outcome.action ? (
              <Button size="sm" onClick={takeTreat}>
                {outcome.action}
              </Button>
            ) : isTricking ? (
              <Button size="sm" variant="outline" onClick={stopTrick}>
                Make it stop!
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={knock}>
                Knock again
              </Button>
            )}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}
